import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import GlobalSearch from "./GlobalSearch";
import NotificationBell from "./NotificationBell";
import { getRoleFlags } from "../hooks/useRole";

/**
 * Chrome around the authenticated agency app: topbar + sidebar + main outlet.
 * Pure presentation — owns no domain data, just routes and the role pill.
 */
export default function AppShell({
  agency,
  agencyStyle,
  currentUser,
  navItems,
  pageTitle,
  jobs,
  users,
  entities,
  notifications,
  unreadCount,
  markRead,
  markAllRead,
  onLogout,
  children,
}) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setAvatarMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeSidebar = () => setSidebarOpen(false);
  const sidebarTitle = agency?.name || "OfficerOnDuty";
  const { isAdmin, isSecretary } = getRoleFlags(currentUser?.role);
  const rolePillClass = isAdmin
    ? "topbar-role-admin"
    : isSecretary
      ? "topbar-role-secretary"
      : "topbar-role-officer";

  const navMatchHelper = (item, location) => {
    return (
      (item.to === "/jobs-hub" && location.pathname.startsWith("/jobs-hub")) ||
      (item.to === "/entities-hub" && location.pathname.startsWith("/entities-hub")) ||
      (item.to === "/audit-hub" && location.pathname.startsWith("/audit-hub"))
    );
  };

  return (
    <div className="app-layout" style={agencyStyle}>
      <a href="#main-content" className="skip-nav">Skip to content</a>

      <div className="mobile-header">
        <button
          className="mobile-hamburger"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={sidebarOpen ? "Close menu" : "Open menu"}
          aria-expanded={sidebarOpen}
        >
          <span className="hamburger-bar" />
          <span className="hamburger-bar" />
          <span className="hamburger-bar" />
        </button>
        <span className="mobile-title">{pageTitle}</span>
        <div className="mobile-header-right">
          <GlobalSearch jobs={jobs} users={users} entities={entities} currentUser={currentUser} />
          <NotificationBell
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkRead={markRead}
            onMarkAllRead={markAllRead}
          />
          <NavLink to="/profile" className="mobile-settings" aria-label="Profile" onClick={closeSidebar}>
            {"⚙️"}
          </NavLink>
        </div>
      </div>

      <header className="topbar">
        <div className="topbar-left">
          <span className="topbar-logo">OfficerOnDuty</span>
          <span className="topbar-org">{agency?.name || "OfficerOnDuty"}</span>
          {agency?.agencyContact && (agency.agencyContact.name || agency.agencyContact.email || agency.agencyContact.phone) && (
            <span className="topbar-contact" title={`Point of Contact: ${[agency.agencyContact.name, agency.agencyContact.email, agency.agencyContact.phone].filter(Boolean).join(" • ")}`}>
              POC: {agency.agencyContact.name || agency.agencyContact.email}
            </span>
          )}
        </div>
        <nav className="topbar-nav">
          {navItems.filter(i => i.to !== "/profile").map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) => {
                const active = isActive || navMatchHelper(item, location);
                return `nav-link ${active ? "nav-link-active" : ""}`;
              }}
            >
              {item.label.replace(/ \(\d+\)$/, "")}
            </NavLink>
          ))}
        </nav>
        <GlobalSearch jobs={jobs} users={users} entities={entities} currentUser={currentUser} />
        <div className="topbar-right">
          <span className={`topbar-role-pill ${rolePillClass}`}>
            {currentUser.role.toUpperCase()}
          </span>
          <NotificationBell
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkRead={markRead}
            onMarkAllRead={markAllRead}
          />
          <div className="topbar-dropdown topbar-avatar-dropdown" ref={avatarRef}>
            <button
              className="topbar-avatar"
              title={currentUser.name}
              onClick={() => setAvatarMenuOpen(!avatarMenuOpen)}
            >
              {currentUser.name ? currentUser.name.split(/\s+/).map(n => n[0]).join("").toUpperCase().slice(0, 2) : "?"}
            </button>
            {avatarMenuOpen && (
              <div className="topbar-dropdown-menu topbar-dropdown-menu-right">
                <NavLink
                  to="/profile"
                  className={({ isActive }) => `topbar-dropdown-item ${isActive ? "topbar-dropdown-item-active" : ""}`}
                  onClick={() => setAvatarMenuOpen(false)}
                >
                  <span className="nav-icon">{"👤"}</span> My Profile
                </NavLink>
                <button className="topbar-dropdown-item topbar-dropdown-signout" onClick={onLogout}>
                  <span className="nav-icon">{"🚪"}</span> Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar} role="presentation" aria-hidden="true" />}

      <aside className={`sidebar ${sidebarOpen ? "sidebar-open" : ""}`} role="navigation" aria-label="Main navigation">
        <div className="sidebar-header">
          {agency?.logoUrl && (
            <img src={agency.logoUrl} alt={`${agency.name} logo`} className="sidebar-logo" />
          )}
          <h3 className="sidebar-title">{sidebarTitle}</h3>
          <p className="sidebar-user">
            {currentUser.name} &middot; {currentUser.role}
          </p>
          {agency?.agencyContact && (agency.agencyContact.name || agency.agencyContact.email || agency.agencyContact.phone) && (
            <div className="sidebar-contact">
              <span className="sidebar-contact-label">Point of Contact</span>
              {agency.agencyContact.name && (
                <span className="sidebar-contact-item">{agency.agencyContact.name}</span>
              )}
              {agency.agencyContact.email && (
                <a href={`mailto:${agency.agencyContact.email}`} className="sidebar-contact-link">{agency.agencyContact.email}</a>
              )}
              {agency.agencyContact.phone && (
                <a href={`tel:${agency.agencyContact.phone}`} className="sidebar-contact-link">{agency.agencyContact.phone}</a>
              )}
            </div>
          )}
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) => {
                const active = isActive || navMatchHelper(item, location);
                return `nav-link ${active ? "nav-link-active" : ""}`;
              }}
              onClick={closeSidebar}
            >
              <span className="nav-icon">{item.icon}</span> {item.label}
            </NavLink>
          ))}
        </nav>
        <button className="logout-btn" onClick={onLogout}>Sign Out</button>
      </aside>

      <main className="app-main" id="main-content">
        {children}
      </main>
    </div>
  );
}
